import { GetServerSideProps } from "next";
import Link from "next/link";

import Grid from "@afa-shared/afa-components/dist/Grid";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Meta from "@components/Meta/meta";
import { Event, trackEvent } from "@utils/events";
import { socialMediaDefaultImage } from "@utils/imageHelpers/imageHelper";
import { getBaseUrl } from "@utils/urlResolver";

import { GetSiteFlatStructure } from "services/SiteFlatStructureService";
import { GetSiteTreeStructure } from "services/SiteTreeStructureFactory";
import { FlatStructureItem } from "types/FlatStructureItem";
import { SiteFlatStructure, SiteTreeStructure } from "types/SiteStructure";
import { SiteStructureItem } from "types/SiteStructureItem";

import webbplatskartaStyles from "./webbplatskarta.module.css";

const SiteMap = (props) => {
  const siteTreeStructure: SiteTreeStructure = props.siteTreeStructure;
  const siteFlatStructure: SiteFlatStructure = props.siteFlatStructure;

  // structure tree with försäkring, arbetsmijö och hälsa, kundtjänst om oss
  const filteredStructureTree = siteTreeStructure?.structureTree?.children?.filter(
    (child: SiteStructureItem) =>
      child?.id === "6bOjkrCBYGXGnGcPkEiiHF" ||
      child?.id === "7xG5l88pobeUQfyGt07UQ6" ||
      child?.id === "5HG7HW5d1XQvSNW3gO77WV" ||
      child?.id === "2st2YT454sTntiMUKD79Zw"
  );

  const redirects = siteFlatStructure?.flatStructure?.filter(
    (child: FlatStructureItem) => child?.redirectsTo
  );

  const getSiteMap = (item: SiteStructureItem, i: number): JSX.Element => {
    //if page is a container render existing children
    if (item?.isContainer) {
      if (item?.children?.length > 0) {
        return (
          <>{item?.children?.map((child: SiteStructureItem, i: number) => getSiteMap(child, i))}</>
        );
      } else {
        return null;
      }
    }

    //if there is another page that redirects to this page, skip this one
    if (
      redirects?.some(
        (redirectedItem: FlatStructureItem) => redirectedItem?.redirectsTo === item?.url
      )
    ) {
      return null;
    }

    //if page redirects to another page, make sure that page exists
    const redirectedItem = siteFlatStructure?.flatStructure?.find(
      (child: FlatStructureItem) => child?.url === item?.redirectsTo
    );

    if (item?.redirectsTo && !redirectedItem) {
      return null;
    }

    //if page has noindex set, dont render this one
    const flatItem = siteFlatStructure?.flatStructure.find((i) => i.slug === item?.slug);
    if (flatItem?.noIndex) {
      return null;
    }
    const children = item?.children?.map((child: SiteStructureItem, i: number) =>
      getSiteMap(child, i)
    );
    return (
      <li key={i}>
        <Link href={redirectedItem ? redirectedItem?.url : item?.url} prefetch={false}>
          {redirectedItem ? redirectedItem?.name : item?.name}
        </Link>
        {children?.length > 0 && <ul>{children}</ul>}
      </li>
    );
  };
  const baseUrl = getBaseUrl();

  return (
    <>
      <Meta
        title={"Webbplatskarta"}
        description={"Webbplatskarta för användare"}
        image={baseUrl + socialMediaDefaultImage?.url}
        url={baseUrl + "/webbplatskarta"}
        imageAlt={socialMediaDefaultImage?.imageAltText}
        noIndex={false}
        noFollow={false}
      />
      <Grid container alignItems={"center"} className={webbplatskartaStyles.contentWrapper}>
        <Grid margin={"0 auto"} desktop={8}>
          <Heading variant="h1" children={"Webbplatskarta"} />
          <div className={webbplatskartaStyles.webbplatskartaWrapper}>
            <ul className={webbplatskartaStyles.webbplatskartaList}>
              <li>
                <Link href={baseUrl} prefetch={false}>
                  Start
                </Link>
              </li>
              {filteredStructureTree?.map((item: SiteStructureItem, i: number) =>
                getSiteMap(item, i)
              )}
            </ul>
          </div>
        </Grid>
      </Grid>
    </>
  );
};

export default SiteMap;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const preview = process.env.FORCE_PREVIEW === "true" || context.preview || false;

  trackEvent(Event.GET_SERVER_SIDE_PROPS, { path: "/webbplatskarta", preview });

  const siteTreeStructure = await GetSiteTreeStructure(preview);
  const siteFlatStructure = await GetSiteFlatStructure(preview);

  return {
    props: {
      siteTreeStructure,
      siteFlatStructure,
    },
  };
};
